import { Controller } from "@hotwired/stimulus"

/**
 * Deep Linking Controller
 * 
 * Handles deep linking functionality for academy sections and posts,
 * including URL hash management, accordion toggling, and browser history.
 */
export default class extends Controller {
  static targets = ["accordion", "panel"]
  
  static values = {
    initialLoadComplete: { type: Boolean, default: false },
    currentSectionId: String
  }

  initialize() {
    // Check if we're on an academy page immediately
    this.onAcademyPage = document.querySelector('.academy-show-page') !== null;
    
    // Don't do anything else if we're not on an academy page
    if (!this.onAcademyPage) {
      console.log("Deep linking controller initialized on non-academy page, disabling");
      // Completely disable all methods to prevent any execution
      this.connect = () => {};
      this.disconnect = () => {};
      this.setupEventListeners = () => {};
      this.handlePopState = () => {};
      this.handleHashChange = () => {};
      this.toggleAccordion = () => {};
      this.handleDeepLinking = () => {};
    }
  }
  
  connect() {
    if (!this.onAcademyPage) return;
    
    console.log("Deep linking controller connected on academy page");
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Handle deep linking on page load
    this.handleDeepLinking();
    
    // Mark initial load as complete
    this.initialLoadCompleteValue = true;
  }
  
  disconnect() {
    if (!this.onAcademyPage) return;
    
    console.log("Deep linking controller disconnected");
    
    // Clean up event listeners
    window.removeEventListener('popstate', this.handlePopState.bind(this));
    window.removeEventListener('hashchange', this.handleHashChange.bind(this));
  }
  
  // We now use this.onAcademyPage property set in initialize()

  /**
   * Set up event listeners for hash changes and popstate events
   */
  setupEventListeners() {
    if (!this.onAcademyPage) {
      return;
    }
    
    // Handle browser back/forward buttons
    window.addEventListener('popstate', this.handlePopState.bind(this));
    
    // Handle hash changes
    window.addEventListener('hashchange', this.handleHashChange.bind(this));
    
    // Set up accordion click handlers
    this.accordionTargets.forEach(accordion => {
      accordion.addEventListener('click', (event) => {
        // Prevent click propagation if the click was on the copy link button
        if (event.target.closest('.copy-link')) {
          event.stopPropagation();
          return;
        }
        this.toggleAccordion(accordion);
      });
    });
  }

  /**
   * Handle popstate events (browser back/forward buttons)
   */
  handlePopState(event) {
    if (this.initialLoadCompleteValue && this.onAcademyPage) {
      this.handleDeepLinking();
    }
  }

  /**
   * Handle hash changes in the URL
   */
  handleHashChange() {
    if (this.initialLoadCompleteValue && this.onAcademyPage) {
      this.handleDeepLinking();
    }
  }

  /**
   * Toggle accordion open/closed state
   * @param {HTMLElement} accordion - The accordion element to toggle
   * @param {boolean} forceOpen - Whether to force the accordion open
   */
  toggleAccordion(accordion, forceOpen = false) {
    if (!accordion) return;
    
    const targetClass = accordion.getAttribute("data-target");
    const panel = document.querySelector(`.${targetClass}`);
    
    if (!panel) return;
    
    // Get section ID for URL updating
    const sectionId = accordion.getAttribute("data-section-id");
    
    // Toggle current panel
    const isActive = accordion.classList.contains("active");
    
    if (isActive && !forceOpen) {
      // If it's already open and not forced to open, close it
      accordion.classList.remove("active");
      // Set height to scrollHeight first to ensure smooth animation
      panel.style.height = `${panel.scrollHeight}px`;
      
      // Force reflow
      panel.offsetHeight;
      
      // Then set to null for animation
      panel.style.height = null;
      
      // Update URL by removing the hash
      if (window.location.hash) {
        history.pushState("", document.title, window.location.pathname + window.location.search);
        this.currentSectionIdValue = "";
      }
    } else {
      // Close all other panels first
      this.accordionTargets.forEach(other => {
        if (other !== accordion) {
          other.classList.remove("active");
          const otherPanel = document.querySelector(`.${other.getAttribute("data-target")}`);
          if (otherPanel) otherPanel.style.height = null;
        }
      });
      
      // Open this panel
      accordion.classList.add("active");
      panel.style.height = panel.scrollHeight + "px";
      
      // Update URL hash
      if (sectionId && sectionId !== this.currentSectionIdValue) {
        // Use pushState instead of replaceState to create history entry
        const newUrl = window.location.pathname + window.location.search + `#section-${sectionId}`;
        history.pushState({sectionId: sectionId}, "", newUrl);
        this.currentSectionIdValue = sectionId;
      }
    }
    
    return panel;
  }

  /**
   * Handle deep linking based on URL hash or query parameters
   */
  handleDeepLinking() {
    // Get the hash from the URL
    const hash = window.location.hash;
    
    // Check for query parameters first (they take precedence)
    const urlParams = new URLSearchParams(window.location.search);
    const sectionIdParam = urlParams.get('section_id');
    const postIdParam = urlParams.get('post_id');
    
    if (postIdParam) {
      // Handle post deep linking from query parameter
      const postElement = document.querySelector(`[data-post-id="${postIdParam}"]`);
      
      if (postElement) {
        // Find the parent section accordion
        const sectionElement = postElement.closest('.section-block').previousElementSibling;
        
        if (sectionElement) {
          // Open the accordion panel and force it to stay open
          this.toggleAccordion(sectionElement, true);
          
          // Scroll to the post
          setTimeout(() => {
            postElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
          
          // Update current section ID
          this.currentSectionIdValue = sectionElement.getAttribute("data-section-id");
        }
      }
      return;
    } else if (sectionIdParam) {
      // Handle section deep linking from query parameter
      const sectionElement = document.querySelector(`[data-section-id="${sectionIdParam}"]`);
      
      if (sectionElement) {
        // Open the accordion panel and force it to stay open
        this.toggleAccordion(sectionElement, true);
        
        // Scroll to the section
        setTimeout(() => {
          sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
        
        // Update current section ID
        this.currentSectionIdValue = sectionIdParam;
      }
      return;
    }
    
    // If no query parameters, check for hash
    if (!hash) return;
    
    // Remove the # symbol
    const targetId = hash.substring(1);
    
    // Check if it's a section or post
    if (targetId.startsWith('section-')) {
      // It's a section
      const sectionId = targetId.replace('section-', '');
      const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
      
      if (sectionElement) {
        // Open the accordion panel and force it to stay open
        this.toggleAccordion(sectionElement, true);
        
        // Scroll to the section
        setTimeout(() => {
          sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
        
        // Update current section ID
        this.currentSectionIdValue = sectionId;
      }
    } else if (targetId.startsWith('post-')) {
      // It's a post
      const postId = targetId.replace('post-', '');
      const postElement = document.querySelector(`[data-post-id="${postId}"]`);
      
      if (postElement) {
        // Find the parent section accordion
        const sectionElement = postElement.closest('.section-block').previousElementSibling;
        
        if (sectionElement) {
          // Open the accordion panel and force it to stay open
          this.toggleAccordion(sectionElement, true);
          
          // Scroll to the post
          setTimeout(() => {
            postElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
          
          // Update current section ID
          this.currentSectionIdValue = sectionElement.getAttribute("data-section-id");
        }
      }
    }
  }
}
